import { useEffect, useRef, useState } from "react";
import copyToClipboard from "copy-to-clipboard";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import { useVoteMessage } from "../../hooks/useVoteMessage";
import type { HelpWidgetMessage } from "../../types/help_widget_message";
import { ChatMessageSources } from "./ChatMessageSources";

export const ChatMessageFooter = ({
  type,
  isLastMessage,
  isAbsoluteLastMessage = false,
  content,
  message,
  conversationId,
  onVoteSuccess,
  isRevealing = false,
  isBotProcessing = false,
}: {
  type: "business" | "bot" | "user" | "system";
  isLastMessage: boolean;
  isAbsoluteLastMessage?: boolean;
  content?: string;
  message?: HelpWidgetMessage;
  conversationId?: string;
  onVoteSuccess?: (
    messageId: string,
    voteType: "upvote" | "downvote" | null,
  ) => void;
  isRevealing?: boolean;
  isBotProcessing?: boolean;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [shouldShowActions, setShouldShowActions] = useState(false);
  const [areSourcesVisible, setAreSourcesVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { mutate: voteMessage } = useVoteMessage({
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  // Debounce showing actions only for the absolute last message in conversation
  useEffect(() => {
    if (type === "user") return;

    // For older messages (not absolute last), show actions immediately
    if (!isAbsoluteLastMessage) {
      setShouldShowActions(true);
      return;
    }

    // For the absolute last message, apply delay and check bot processing
    if (isRevealing || isBotProcessing) {
      // Hide actions immediately when bot is processing or message is revealing
      setShouldShowActions(false);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    } else {
      // Wait 500ms after bot finishes before showing actions
      debounceTimerRef.current = setTimeout(() => {
        setShouldShowActions(true);
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isRevealing, isBotProcessing, type, isAbsoluteLastMessage]);

  const handleCopy = () => {
    if (content) {
      copyToClipboard(content);
      setIsCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    }
  };

  const handleToggleSources = () => {
    setAreSourcesVisible(prev => !prev);
  };

  const handleVote = (voteType: "upvote" | "downvote") => {
    if (!message || !conversationId) return;

    const currentVote = message.vote;
    const newVote = currentVote === voteType ? null : voteType;

    // Optimistically update the UI
    onVoteSuccess?.(message.id, newVote);

    // Make the API call
    voteMessage({
      conversationId,
      messageId: message.id,
      voteType,
    });
  };

  const renderCopyButton = () => (
    <TippyV2
      content={t("help_widget.copilot.help_widget_message.copy")}
      placement="top"
      interactive={false}
      shouldUseSpanWrapper={false}
    >
      <button
        type="button"
        onClick={handleCopy}
        className="hover:bg-secondary flex size-6 cursor-pointer items-center justify-center rounded-3xl bg-transparent transition-all duration-200 ease-out"
      >
        <Icon type={isCopied ? "hw-copied" : "hw-copy"} size={16} />
      </button>
    </TippyV2>
  );

  // if we are here, it means its non user message
  if (!isLastMessage || !shouldShowActions || type === "user") {
    return null;
  }

  const renderSourcesButton = () => {
    if (!message?.sources || message.sources.length === 0) return null;

    const buttonText = areSourcesVisible
      ? t("help_widget.conversation_page.hide_sources")
      : t("help_widget.conversation_page.show_sources");

    return (
      <button
        type="button"
        onClick={handleToggleSources}
        className="hover:bg-secondary flex cursor-pointer items-center gap-1 rounded-3xl bg-transparent px-3 py-1 transition-all duration-200 ease-out"
      >
        <Icon type="hw-sources" size={16} />
        <Typography.BodyXs color="text-darkest">{buttonText}</Typography.BodyXs>
      </button>
    );
  };

  const currentVote = message?.vote;

  const renderVoteButtons = () => {
    if (currentVote === "upvote") {
      return (
        <TippyV2
          content={t("help_widget.copilot.help_widget_message.helpful")}
          placement="top"
          interactive={false}
          shouldUseSpanWrapper={false}
        >
          <button
            type="button"
            className="hover:bg-secondary flex size-6 cursor-pointer items-center justify-center rounded-3xl bg-transparent transition-all duration-300 ease-in-out"
            onClick={() => handleVote("upvote")}
          >
            <Icon type="hw-up-voted" size={16} />
          </button>
        </TippyV2>
      );
    }

    if (currentVote === "downvote") {
      return (
        <TippyV2
          content={t("help_widget.copilot.help_widget_message.unhelpful")}
          placement="top"
          interactive={false}
          shouldUseSpanWrapper={false}
        >
          <button
            type="button"
            className="hover:bg-secondary flex size-6 cursor-pointer items-center justify-center rounded-3xl bg-transparent transition-all duration-300 ease-out"
            onClick={() => handleVote("downvote")}
          >
            <Icon type="hw-down-voted" size={16} />
          </button>
        </TippyV2>
      );
    }

    return (
      <>
        <TippyV2
          content={t("help_widget.copilot.help_widget_message.helpful")}
          placement="top"
          interactive={false}
          shouldUseSpanWrapper={false}
        >
          <button
            type="button"
            className="hover:bg-secondary flex size-6 cursor-pointer items-center justify-center rounded-3xl bg-transparent transition-all duration-300 ease-out"
            onClick={() => handleVote("upvote")}
          >
            <Icon type="hw-up-vote" size={16} />
          </button>
        </TippyV2>
        <TippyV2
          content={t("help_widget.copilot.help_widget_message.unhelpful")}
          placement="top"
          interactive={false}
          shouldUseSpanWrapper={false}
        >
          <button
            type="button"
            className="hover:bg-secondary flex size-6 cursor-pointer items-center justify-center rounded-3xl bg-transparent transition-all duration-300 ease-out"
            onClick={() => handleVote("downvote")}
          >
            <Icon type="hw-down-vote" size={16} />
          </button>
        </TippyV2>
      </>
    );
  };

  const BUTTON_SIZE = 24; // size-6 in Tailwind (w-6 h-6)
  const BUTTON_GAP = 5; // gap-[5px]
  const voteButtonsWidth = currentVote
    ? BUTTON_SIZE
    : BUTTON_SIZE * 2 + BUTTON_GAP;

  return (
    <>
      {message?.sources && (
        <ChatMessageSources
          sources={message.sources}
          areSourcesVisible={areSourcesVisible}
        />
      )}
      <div className="text-darkest animate-fade-in-slow mt-1.5 flex items-center gap-[5px]">
        {renderSourcesButton()}
        <div
          className="flex gap-[5px] overflow-hidden transition-all duration-300 ease-out"
          style={{
            width: `${voteButtonsWidth}px`,
          }}
        >
          {renderVoteButtons()}
        </div>
        <div
          style={{
            transition: "opacity 0.3s ease-out",
          }}
        >
          {renderCopyButton()}
        </div>
      </div>
    </>
  );
};
